import React, { useState } from 'react';
import { 
  FaBriefcase, 
  FaMapMarkerAlt, 
  FaClock, 
  FaCheckCircle,
  FaArrowRight,
  FaUsers,
  FaLightbulb,
  FaHeart
} from 'react-icons/fa';

const Careers = () => {
  const [selectedJob, setSelectedJob] = useState(null);

  const jobOpenings = [
    {
      id: 1,
      title: "Senior Full Stack Developer",
      department: "Engineering",
      location: "Remote",
      type: "Full-time",
      experience: "5+ years",
      description: "We're looking for a passionate full-stack developer to join our engineering team and help build the future of waste management technology.",
      responsibilities: [
        "Develop and maintain our web and mobile applications",
        "Design and implement scalable backend systems",
        "Collaborate with cross-functional teams",
        "Mentor junior developers",
        "Participate in code reviews and technical discussions"
      ],
      requirements: [
        "5+ years of experience with React, Node.js, and MongoDB",
        "Strong understanding of RESTful APIs and microservices",
        "Experience with cloud platforms (AWS, Azure, or GCP)",
        "Knowledge of DevOps practices and CI/CD pipelines",
        "Excellent problem-solving and communication skills"
      ]
    },
    {
      id: 2,
      title: "Sustainability Manager",
      department: "Operations",
      location: "Lagos, Nigeria",
      type: "Full-time",
      experience: "3+ years",
      description: "Join our operations team to drive sustainability initiatives and ensure our platform meets environmental standards.",
      responsibilities: [
        "Develop and implement sustainability strategies",
        "Monitor environmental impact metrics",
        "Collaborate with external environmental organizations",
        "Train team members on sustainable practices",
        "Ensure compliance with environmental regulations"
      ],
      requirements: [
        "Degree in Environmental Science or related field",
        "3+ years of experience in sustainability management",
        "Knowledge of environmental regulations and standards",
        "Strong analytical and reporting skills",
        "Passion for environmental conservation"
      ]
    },
    {
      id: 3,
      title: "Customer Success Manager",
      department: "Customer Experience",
      location: "Remote",
      type: "Full-time",
      experience: "2+ years",
      description: "Help our users succeed on our platform by providing exceptional support and building lasting relationships.",
      responsibilities: [
        "Onboard new users and provide training",
        "Resolve customer issues and provide support",
        "Gather user feedback and communicate with product team",
        "Develop customer success strategies",
        "Maintain high customer satisfaction scores"
      ],
      requirements: [
        "2+ years of customer success experience",
        "Excellent communication and interpersonal skills",
        "Experience with CRM systems",
        "Strong problem-solving abilities",
        "Passion for helping others succeed"
      ]
    },
    {
      id: 4,
      title: "Marketing Specialist",
      department: "Marketing",
      location: "Lagos, Nigeria",
      type: "Full-time",
      experience: "2+ years",
      description: "Help us spread the word about sustainable waste management and grow our community of users.",
      responsibilities: [
        "Develop and execute marketing campaigns",
        "Manage social media presence",
        "Create engaging content for various channels",
        "Analyze marketing metrics and optimize strategies",
        "Collaborate with external partners and influencers"
      ],
      requirements: [
        "2+ years of marketing experience",
        "Strong writing and content creation skills",
        "Experience with social media management",
        "Knowledge of digital marketing tools",
        "Creative thinking and analytical skills"
      ]
    }
  ];

  const benefits = [
    {
      icon: <FaUsers className="h-8 w-8 text-blue-600" />,
      title: "Collaborative Culture",
      description: "Work with passionate people who share your values and vision for a sustainable future."
    },
    {
      icon: <FaLightbulb className="h-8 w-8 text-yellow-600" />,
      title: "Innovation Opportunities",
      description: "Be part of cutting-edge technology that's transforming the waste management industry."
    },
    {
      icon: <FaHeart className="h-8 w-8 text-red-600" />,
      title: "Purpose-Driven Work",
      description: "Make a real impact on the environment and communities through meaningful work."
    },
    {
      icon: <FaBriefcase className="h-8 w-8 text-green-600" />,
      title: "Career Growth",
      description: "Access to learning opportunities, mentorship, and clear career progression paths."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Join Our Team</h1>
            <p className="text-xl md:text-2xl text-purple-100 max-w-3xl mx-auto">
              Build the future of sustainable waste management with us
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Why Work With Us */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Work With Us?</h2>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed mb-12">
            At Jhonks, we're not just building a platform – we're creating a movement towards 
            a more sustainable future. Join a team that's passionate about making a real difference 
            in the world.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 text-center">
                <div className="flex justify-center mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Job Openings */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Current Openings</h2>
          <div className="space-y-6">
            {jobOpenings.map((job) => (
              <div key={job.id} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{job.title}</h3>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                      <span className="flex items-center">
                        <FaBriefcase className="h-4 w-4 mr-1" />
                        {job.department}
                      </span>
                      <span className="flex items-center">
                        <FaMapMarkerAlt className="h-4 w-4 mr-1" />
                        {job.location}
                      </span>
                      <span className="flex items-center">
                        <FaClock className="h-4 w-4 mr-1" />
                        {job.type}
                      </span>
                      <span className="flex items-center">
                        <FaCheckCircle className="h-4 w-4 mr-1" />
                        {job.experience}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedJob(selectedJob === job.id ? null : job.id)}
                    className="mt-4 md:mt-0 bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
                  >
                    {selectedJob === job.id ? 'Hide Details' : 'View Details'}
                    <FaArrowRight className={`h-4 w-4 ml-2 transition-transform ${selectedJob === job.id ? 'rotate-90' : ''}`} />
                  </button>
                </div>
                
                {selectedJob === job.id && (
                  <div className="border-t pt-6">
                    <p className="text-gray-700 mb-6">{job.description}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Key Responsibilities</h4>
                        <ul className="space-y-2">
                          {job.responsibilities.map((responsibility, index) => (
                            <li key={index} className="flex items-start">
                              <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              <span className="text-gray-600">{responsibility}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Requirements</h4>
                        <ul className="space-y-2">
                          {job.requirements.map((requirement, index) => (
                            <li key={index} className="flex items-start">
                              <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              <span className="text-gray-600">{requirement}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    <div className="mt-6 flex flex-col sm:flex-row gap-4">
                      <button className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors">
                        Apply Now
                      </button>
                      <button className="border border-purple-600 text-purple-600 px-6 py-3 rounded-lg hover:bg-purple-50 transition-colors">
                        Save Job
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Application Process */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Application Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">1</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Apply Online</h3>
              <p className="text-gray-600">Submit your application through our online portal</p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">2</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Initial Review</h3>
              <p className="text-gray-600">Our team reviews your application and qualifications</p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Interview</h3>
              <p className="text-gray-600">Meet with our team for a technical and cultural fit interview</p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">4</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Decision</h3>
              <p className="text-gray-600">We'll notify you of our decision within 2 weeks</p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg text-white p-8">
          <h2 className="text-3xl font-bold mb-6">Don't See Your Dream Job?</h2>
          <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
            We're always looking for talented individuals who share our vision. 
            Send us your resume and let us know how you'd like to contribute to our mission.
          </p>
          <button className="bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
            Send Us Your Resume
          </button>
        </div>
      </div>
    </div>
  );
};

export default Careers;
